import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import RoundedButton from '@components/RoundedButton';
import TextInputBox from '@components/TextInputBox';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useResponseHandler } from '@hooks/useResponseHandler';

import { useBeginShareAuthMutation } from 'generated/shared_resource.generated';

interface VerifyEmailProps {
  share_id: string;
  onComplete: (email: string) => void;
}

interface FormValues {
  email: string;
}

const messages = defineMessages({
  subtitle: {
    id: 'sharedResource.verifyEmail.subtitle',
    defaultMessage:
      "To view the record, you'll need to verify your email address.",
  },
  header: {
    id: 'sharedResource.verifyEmail.header',
    defaultMessage: 'Enter your email address',
  },
  submitText: {
    id: 'sharedResource.verifyEmail.submitText',
    defaultMessage: 'Send code',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'Required',
  },
  exampleEmail: {
    id: 'passwordReset.emailForm.exampleEmail',
    defaultMessage: 'example@email.com',
  },
  invalidEmail: {
    id: 'GLOBAL.invalidEmail',
    defaultMessage: 'Invalid email',
  },
});

function VerifyEmail({ share_id, onComplete }: VerifyEmailProps) {
  const { formatMessage } = useIntl();
  const { emailSchema } = useCommonSchemas();
  const { handleError } = useResponseHandler({ handlerType: 'snackbar' });
  const [beginShareAuth] = useBeginShareAuthMutation();

  const handleSubmit = async (values: FormValues) => {
    const { email } = values;
    const { errors } = await beginShareAuth({
      variables: {
        input: {
          email: email.toLowerCase(),
          share_id,
        },
      },
    });
    if (errors) {
      handleError(errors);
    }
    onComplete(email);
  };

  const validationSchema = Yup.object().shape({
    email: emailSchema.min(1).required(formatMessage(messages.invalidEmail)),
  });

  const initialValues: FormValues = {
    email: '',
  };

  return (
    <Stack spacing={3}>
      <Typography color="primary.contrastText" variant="h5">
        {formatMessage(messages.subtitle)}
      </Typography>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form noValidate>
            <Card sx={{ maxWidth: 500 }}>
              <CardHeader title={formatMessage(messages.header)} />
              <Divider />

              <CardContent>
                <Field name="email">
                  {({ field, meta }: FieldProps<string>) => (
                    <TextInputBox
                      autoComplete="email"
                      hasError={Boolean(meta.touched && meta.error)}
                      helperText={meta.touched ? meta.error : undefined}
                      placeholder={formatMessage(messages.exampleEmail)}
                      type="email"
                      {...field}
                    />
                  )}
                </Field>
              </CardContent>
              <Divider />
              <CardActions>
                <RoundedButton disabled={isSubmitting} type="submit">
                  {formatMessage(messages.submitText)}
                </RoundedButton>
              </CardActions>
            </Card>
          </Form>
        )}
      </Formik>
    </Stack>
  );
}

export default VerifyEmail;
